import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { liberacaoService } from '../services';
import { 
  Package,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Building2
} from 'lucide-react';

const SeletorLotes = ({ isOpen, onClose, onSelect, medicamento }) => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && medicamento) {
      carregarLotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, medicamento]);

  const carregarLotes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await liberacaoService.getLotesMedicamento(medicamento.medicamentoRegistrado.id);
      setLotes(response.lotes || []);
      
      if (!response.lotes || response.lotes.length === 0) {
        setError('Não há lotes em estoque para este medicamento');
      }
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      setError('Erro ao carregar lotes do medicamento');
      setLotes([]);
    } finally {
      setLoading(false);
    }
  };

  const selecionarLote = (lote) => {
    // Chamar a função de seleção com o medicamento e o lote escolhido
    onSelect(medicamento, lote);
    onClose();
  };

  const formatarData = (data) => {
    if (!data) return 'Não informado';
    try {
      return new Date(data).toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  const calcularDiasVencimento = (dataValidade) => {
    if (!dataValidade) return null;
    const hoje = new Date();
    const validade = new Date(dataValidade);
    const diffTime = validade - hoje;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusValidade = (diasVencimento) => {
    if (diasVencimento === null) return 'normal';
    if (diasVencimento <= 0) return 'vencido';
    if (diasVencimento <= 30) return 'proximo-vencimento';
    return 'normal';
  };

  const getCorStatus = (status) => {
    switch (status) {
      case 'vencido': return '#dc3545';
      case 'proximo-vencimento': return '#ffc107';
      default: return '#28a745';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
      <div style={{ 
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0
        }}>
          <Package size={24} color="#007bff" />
          <div>
            <h4 style={{ margin: 0, fontSize: '18px' }}>Selecionar Lote</h4>
            <p style={{ margin: '5px 0 0 0', color: '#667', fontSize: '14px' }}>
              {medicamento?.medicamentoRegistrado?.nome}
            </p>
          </div>
        </div>

        <div style={{ 
          padding: '20px',
          flex: 1,
          overflowY: 'auto',
          minHeight: 0
        }}>
          {loading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: '#667'
            }}>
              Carregando lotes disponíveis...
            </div>
          )}

          {error && (
            <div style={{ 
              padding: '20px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24',
              textAlign: 'center'
            }}>
              <AlertTriangle size={20} style={{ marginRight: '8px' }} />
              {error}
            </div>
          )}

          {!loading && !error && lotes.length > 0 && (
            <div style={{ 
              display: 'grid', 
              gap: '16px',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))'
            }}>
              {lotes.map((lote) => {
                const diasVencimento = calcularDiasVencimento(lote.dataValidade);
                const status = getStatusValidade(diasVencimento);
                const podeSelecionar = status !== 'vencido';

                return (
                  <div
                    key={lote.id}
                    onClick={() => podeSelecionar ? selecionarLote(lote) : null}
                    className="lote-card"
                    style={{
                      border: '2px solid',
                      borderColor: getCorStatus(status),
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: podeSelecionar ? 'pointer' : 'not-allowed',
                      backgroundColor: podeSelecionar ? 'white' : '#f8f9fa',
                      opacity: podeSelecionar ? 1 : 0.6,
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (podeSelecionar && e.target.classList.contains('lote-card')) {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (podeSelecionar && e.target.classList.contains('lote-card')) {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {/* Indicador de status no canto */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderStyle: 'solid',
                      borderWidth: '0 30px 30px 0',
                      borderColor: `transparent ${getCorStatus(status)} transparent transparent`
                    }} />
                    
                    <div style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {/* Header do lote */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        marginBottom: '4px'
                      }}>
                        <Package size={18} color="#667" />
                        <span style={{ 
                          fontWeight: 'bold',
                          fontSize: '16px',
                          color: '#333'
                        }}>
                          Lote: {lote.lote || 'Não informado'}
                        </span>
                      </div>
                      
                      {/* Informações do lote */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px'
                        }}>
                          <Calendar size={16} color="#667" />
                          <span style={{ fontSize: '14px', color: '#555' }}>
                            Validade: {formatarData(lote.dataValidade)}
                            {diasVencimento !== null && (
                              <span style={{ 
                                marginLeft: '8px',
                                color: getCorStatus(status),
                                fontWeight: 'bold',
                                fontSize: '13px'
                              }}>
                                ({diasVencimento > 0 ? `${diasVencimento} dias` : 'VENCIDO'})
                              </span>
                            )}
                          </span>
                        </div>

                        {lote.fabricante && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px'
                          }}>
                            <Building2 size={16} color="#667" />
                            <span style={{ fontSize: '14px', color: '#667' }}>
                              {lote.fabricante}
                            </span>
                          </div>
                        )}

                        {/* Estoque */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px'
                        }}>
                          <span style={{ fontSize: '12px', color: '#667' }}>
                            Estoque disponível:
                          </span>
                          <span style={{ 
                            fontSize: '18px', 
                            fontWeight: 'bold',
                            color: lote.estoque > 10 ? '#28a745' : lote.estoque > 0 ? '#ffc107' : '#dc3545'
                          }}>
                            {lote.estoque}
                          </span>
                        </div>
                        
                        {/* Status do lote */}
                        <div style={{ 
                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          backgroundColor: podeSelecionar ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                          border: `1px solid ${getCorStatus(status)}`
                        }}>
                          {podeSelecionar ? (
                            <>
                              <CheckCircle size={16} color="#28a745" />
                              <span style={{ 
                                fontSize: '12px', 
                                color: '#28a745',
                                fontWeight: 'bold'
                              }}>
                                Disponível
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={16} color="#dc3545" />
                              <span style={{ 
                                fontSize: '12px', 
                                color: '#dc3545',
                                fontWeight: 'bold'
                              }}>
                                Vencido
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {!podeSelecionar && (
                      <div style={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px'
                      }}>
                        <div style={{
                          backgroundColor: 'rgba(220, 53, 69, 0.9)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <AlertTriangle size={14} />
                          LOTE VENCIDO
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ 
          padding: '16px 20px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
          backgroundColor: '#f8f9fa'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SeletorLotes;